import React, { useState, useEffect } from 'react';
import { X, BookOpen, PackageCheck, AlertTriangle, Download, Check, ChevronRight, ChevronLeft, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface AuditReadinessWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  { id: 1, label: 'Select Framework',         icon: BookOpen },
  { id: 2, label: 'Trigger Evidence Harvest',  icon: PackageCheck },
  { id: 3, label: 'Review Compliance Drift',   icon: AlertTriangle },
  { id: 4, label: 'Export Audit Package',      icon: Download },
];

type Framework = 'soc2' | 'iso27001';

interface DriftEvent {
  id: string | number;
  severity: string;
  label: string;
  framework: string;
  resolved: boolean;
}

interface HarvestSource {
  id: string | number;
  name: string;
  source_type: string;
  status: 'synced' | 'error';
  items_collected?: number;
  error?: string;
}

const STORAGE_KEY = 'wizard_audit_readiness';

interface SavedState {
  step: number;
  completed: number[];
  framework: Framework;
  harvestDone: boolean;
  harvestSources: HarvestSource[];
  totalSynced: number;
  exportDone: boolean;
}

function loadSaved(): SavedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedState) : null;
  } catch {
    return null;
  }
}

const AuditReadinessWizard: React.FC<AuditReadinessWizardProps> = ({ isOpen, onClose }) => {
  // Lazy initialisers — loadSaved() reads localStorage only on initial mount per state slot
  const [step, setStep] = useState<number>(() => loadSaved()?.step ?? 1);
  const [completed, setCompleted] = useState<Set<number>>(() => new Set(loadSaved()?.completed ?? []));

  // Step 1
  const [framework, setFramework] = useState<Framework>(() => loadSaved()?.framework ?? 'soc2');

  // Step 2
  const [harvesting, setHarvesting] = useState(false);
  const [harvestDone, setHarvestDone] = useState<boolean>(() => loadSaved()?.harvestDone ?? false);
  const [harvestSources, setHarvestSources] = useState<HarvestSource[]>(() => loadSaved()?.harvestSources ?? []);
  const [totalSynced, setTotalSynced] = useState<number>(() => loadSaved()?.totalSynced ?? 0);

  // Step 3 — drift items are always re-fetched fresh (they change as you resolve items)
  const [driftItems, setDriftItems] = useState<DriftEvent[]>([]);
  const [driftLoading, setDriftLoading] = useState(false);
  const [resolvingIds, setResolvingIds] = useState<Set<string | number>>(new Set());
  const openCritical = driftItems.filter(d => !d.resolved && (d.severity === 'high' || d.severity === 'critical')).length;
  const canExport = openCritical === 0;

  // Step 4
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState<boolean>(() => loadSaved()?.exportDone ?? false);
  const [packageSummary, setPackageSummary] = useState<Record<string, number | string> | null>(null);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (!isOpen) return;
    const state: SavedState = {
      step,
      completed: Array.from(completed),
      framework,
      harvestDone,
      harvestSources,
      totalSynced,
      exportDone,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [isOpen, step, completed, framework, harvestDone, harvestSources, totalSynced, exportDone]);

  if (!isOpen) return null;

  const stepValid = (s: number) => completed.has(s);

  const canAdvance = () => {
    if (step === 1) return true;
    if (step === 2) return harvestDone;
    if (step === 3) return canExport;
    return false;
  };

  const advance = async () => {
    if (!canAdvance()) return;
    setCompleted(prev => new Set(prev).add(step));
    if (step === 2) {
      setStep(3);
      await loadDrift();
    } else {
      setStep(step + 1);
    }
  };

  const loadDrift = async () => {
    setDriftLoading(true);
    try {
      const res = await api.get('/api/admin/wizards/audit/drift');
      const events: DriftEvent[] = (res.data.events || []).map((e: any) => ({
        id: e.id,
        severity: e.severity,
        label: e.label || e.title || e.description || 'Drift event',
        framework: e.framework || '',
        resolved: false,
      }));
      setDriftItems(events);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to load compliance drift');
    } finally {
      setDriftLoading(false);
    }
  };

  const triggerHarvest = async () => {
    setHarvesting(true);
    try {
      const res = await api.post('/api/admin/wizards/audit/harvest');
      setHarvestSources(res.data.sources || []);
      setTotalSynced(res.data.totalSynced || 0);
      setHarvestDone(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Evidence harvest failed');
    } finally {
      setHarvesting(false);
    }
  };

  const resolveItem = async (id: string | number) => {
    setResolvingIds(prev => new Set(prev).add(id));
    try {
      await api.put(`/api/admin/wizards/audit/drift/${id}/resolve`);
      setDriftItems(prev => prev.map(d => d.id === id ? { ...d, resolved: true } : d));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to resolve item');
    } finally {
      setResolvingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const triggerExport = async () => {
    setExporting(true);
    try {
      // Fetch as JSON so we can read the summary AND trigger a download
      const res = await api.post('/api/admin/wizards/audit/export', { framework });
      const packageData = res.data;

      // Store live summary for display
      if (packageData?.summary) {
        setPackageSummary(packageData.summary);
      }

      // Trigger browser download from the JSON response
      const blob = new Blob([JSON.stringify(packageData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = `audit-package-${framework}-${new Date().toISOString().slice(0, 10)}.json`;
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setExportDone(true);
      setCompleted(prev => new Set(prev).add(4));
      localStorage.removeItem(STORAGE_KEY);
      toast.success('Audit package downloaded successfully');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Audit export failed');
    } finally {
      setExporting(false);
    }
  };

  const frameworkLabel: Record<Framework, string> = { soc2: 'SOC 2 Type II', iso27001: 'ISO 27001' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Audit Readiness Check</h2>
            <p className="text-purple-200 text-xs mt-0.5">Generate a complete audit-ready package. The wizard flags open compliance drift items that must be resolved before the audit package can be exported.</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step progress */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center gap-0">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = stepValid(s.id);
              return (
                <React.Fragment key={s.id}>
                  <button onClick={() => isDone ? setStep(s.id) : undefined}
                    className={`flex flex-col items-center min-w-0 flex-1 ${isDone ? 'cursor-pointer' : 'cursor-default'}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                      isDone ? 'bg-purple-600 border-purple-600 text-white' :
                      isActive ? 'bg-white border-purple-600 text-purple-600' :
                      'bg-gray-100 border-gray-200 text-gray-400'
                    }`}>
                      {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] font-medium mt-1 text-center leading-tight ${isActive ? 'text-purple-700' : isDone ? 'text-purple-500' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 rounded transition-all ${stepValid(s.id) ? 'bg-purple-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-4 min-h-[260px] max-h-[360px] overflow-y-auto">

          {/* Step 1 — Select Framework */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-purple-600" />Select Framework</h3>
              <div className="grid grid-cols-2 gap-4">
                {([{v:'soc2',l:'SOC 2 Type II',desc:'Trust Services Criteria — CC series controls'},{v:'iso27001',l:'ISO 27001',desc:'Information security management standard'}] as const).map(({v,l,desc}) => (
                  <button key={v} onClick={() => setFramework(v)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${framework === v ? 'bg-purple-50 border-purple-500' : 'border-gray-200 hover:border-purple-300'}`}>
                    <p className={`font-semibold text-sm ${framework === v ? 'text-purple-700' : 'text-gray-900'}`}>{l}</p>
                    <p className="text-xs text-gray-500 mt-1">{desc}</p>
                    {framework === v && <div className="mt-2 flex items-center gap-1 text-purple-600 text-xs font-medium"><Check className="w-3 h-3" />Selected</div>}
                  </button>
                ))}
              </div>
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-xs text-purple-800">Preparing audit package for: <strong>{frameworkLabel[framework]}</strong>. Evidence will be tagged to the relevant control IDs for this framework.</p>
              </div>
            </div>
          )}

          {/* Step 2 — Trigger Evidence Harvest */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><PackageCheck className="w-4 h-4 text-purple-600" />Trigger Evidence Harvest</h3>
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                <p className="text-sm text-gray-700">This will sync all active evidence sources and collect new items tagged to <strong>{frameworkLabel[framework]}</strong> controls.</p>
                {harvestSources.length > 0 && (
                  <div className="space-y-1.5">
                    {harvestSources.map(src => (
                      <div key={src.id} className={`flex items-center justify-between px-3 py-1.5 rounded-lg border text-xs font-medium ${src.status === 'synced' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        <span className="flex items-center gap-1.5">
                          {src.status === 'synced' ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                          {src.name}
                        </span>
                        <span>{src.status === 'synced' ? `${src.items_collected ?? 0} items` : src.error || 'error'}</span>
                      </div>
                    ))}
                  </div>
                )}
                {!harvestDone && harvestSources.length === 0 && (
                  <p className="text-xs text-gray-500">Run the harvest to sync all active evidence sources.</p>
                )}
              </div>
              {!harvestDone ? (
                <button onClick={triggerHarvest} disabled={harvesting}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-purple-600 text-white rounded-xl font-medium text-sm hover:bg-purple-700 disabled:opacity-60 transition-colors">
                  {harvesting ? <><Loader2 className="w-4 h-4 animate-spin" />Harvesting evidence…</> : <><PackageCheck className="w-4 h-4" />Run Evidence Harvest</>}
                </button>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  <p className="text-sm text-green-800 font-medium">Harvest complete — {totalSynced.toLocaleString()} evidence items collected and tagged.</p>
                </div>
              )}
              {!harvestDone && <p className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Run the harvest before proceeding to the next step.</p>}
            </div>
          )}

          {/* Step 3 — Review Compliance Drift */}
          {step === 3 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-purple-600" />Review Compliance Drift
                {!driftLoading && openCritical > 0 && (
                  <span className="ml-auto text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">{openCritical} HIGH must be resolved</span>
                )}
                {!driftLoading && openCritical === 0 && (
                  <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">All clear</span>
                )}
              </h3>
              {driftLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-purple-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading compliance drift events…</span>
                </div>
              ) : driftItems.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  <p className="text-sm text-green-800 font-medium">No open compliance drift events. Ready to export.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {driftItems.map(item => (
                    <div key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${item.resolved ? 'bg-green-50 border-green-200 opacity-60' : (item.severity === 'high' || item.severity === 'critical') ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                      <div className={`mt-0.5 text-xs font-bold px-1.5 py-0.5 rounded shrink-0 ${item.resolved ? 'bg-green-200 text-green-700' : (item.severity === 'high' || item.severity === 'critical') ? 'bg-red-200 text-red-700' : 'bg-yellow-200 text-yellow-700'}`}>
                        {item.resolved ? 'RESOLVED' : item.severity}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium">{item.label}</p>
                        <p className="text-xs text-gray-500">{item.framework}</p>
                      </div>
                      {!item.resolved && (
                        <button
                          onClick={() => resolveItem(item.id)}
                          disabled={resolvingIds.has(item.id)}
                          className="shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2 disabled:opacity-50">
                          {resolvingIds.has(item.id) ? 'Resolving…' : 'Mark resolved'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!driftLoading && !canExport && openCritical > 0 && (
                <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Resolve all HIGH items before the audit package can be exported.</p>
              )}
            </div>
          )}

          {/* Step 4 — Export Audit Package */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2"><Download className="w-4 h-4 text-purple-600" />Export Audit Package</h3>

              {/* Pre-export: show what will be included */}
              {!exportDone && !packageSummary && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
                  <p className="text-sm text-gray-700 font-medium">Package will include for <strong>{frameworkLabel[framework]}</strong>:</p>
                  <ul className="space-y-1.5 text-xs text-gray-600">
                    {[
                      'Evidence inventory with control ID tags',
                      'Compliance drift report — all HIGH items resolved',
                      'Vendor audit report (SOC2/DPA/risk matrix)',
                      'Asset inventory & BYOD policy',
                      'Privilege access quarterly review (90 days)',
                      'Tenant isolation live report',
                      'AI activity log export (90 days)',
                    ].map(item => (
                      <li key={item} className="flex items-start gap-1.5"><Check className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Post-export: show live stats from the actual package */}
              {exportDone && packageSummary && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl space-y-2">
                  <p className="text-xs font-semibold text-green-800 mb-2">Package contents — live counts</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      { label: 'Evidence items', value: packageSummary.active_evidence_items },
                      { label: 'Evidence sources', value: packageSummary.active_evidence_sources },
                      { label: 'Open drift items', value: packageSummary.open_drift_items },
                      { label: 'Resolved drift items', value: packageSummary.resolved_drift_items },
                      { label: 'Vendors reviewed', value: packageSummary.total_vendors },
                      { label: 'Assets inventoried', value: packageSummary.total_assets },
                      { label: 'Privileged access events (90d)', value: packageSummary.privileged_access_events_90d },
                      { label: 'AI queries (90d)', value: packageSummary.ai_queries_90d },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between bg-white rounded-lg px-2 py-1.5 border border-green-100">
                        <span className="text-gray-600">{label}</span>
                        <span className="font-bold text-gray-900">{value ?? '—'}</span>
                      </div>
                    ))}
                  </div>
                  <div className={`flex items-center gap-1.5 mt-1 text-xs font-semibold px-2 py-1 rounded-lg ${packageSummary.tenant_isolation === 'COMPLIANT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    <Check className="w-3 h-3" />Tenant Isolation: {String(packageSummary.tenant_isolation)}
                  </div>
                </div>
              )}

              {!exportDone ? (
                <button onClick={triggerExport} disabled={exporting}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-purple-600 text-white rounded-xl font-medium text-sm hover:bg-purple-700 disabled:opacity-60 transition-colors">
                  {exporting ? <><Loader2 className="w-4 h-4 animate-spin" />Generating package…</> : <><Download className="w-4 h-4" />Export Audit Package (JSON)</>}
                </button>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  <p className="text-sm text-green-800 font-medium">Audit package downloaded successfully.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <button onClick={() => step > 1 && setStep(step - 1)} disabled={step === 1}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4" />Back
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map(s => (
              <div key={s.id} className={`w-2 h-2 rounded-full transition-all ${step === s.id ? 'bg-purple-600 w-4' : stepValid(s.id) ? 'bg-purple-300' : 'bg-gray-200'}`} />
            ))}
          </div>
          {step < 4 ? (
            <button onClick={advance} disabled={!canAdvance() || driftLoading}
              className="flex items-center gap-1.5 px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next<ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={onClose} disabled={!exportDone}
              className="flex items-center gap-1.5 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Check className="w-4 h-4" />Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditReadinessWizard;
